/**
 * Outil « Calcul matriciel » (Boîte à outils, Tle maths expertes → prépa).
 *
 * Matrices 2 × 2 qu'on remplit (entiers, décimaux, fractions) : produit
 * ligne × colonne case par case (et AB ≠ BA), inverse par le déterminant,
 * système AX = Y, puissances Aⁿ et suite Uₙ₊₁ = AUₙ. Tout est exact.
 */

import { useState } from 'react';
import { MathText } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, Segmented, Stepper, ToggleChip } from '../../ui/controls';
import { frenchSpaces, superscript } from '../../ui/format';
import {
  atoms,
  cellTex,
  det2,
  factorTex,
  inv2,
  isZero,
  matEq,
  matMul,
  matPow,
  matTex,
  parseQ,
  show,
  solve,
  type Mat,
  type Q,
} from './matrix';
import { Live, MatrixGrid, MatrixInput } from './matrix-ui';

type Mode = 'product' | 'inverse' | 'system' | 'power';

const MODES: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: 'product', label: 'Produit' },
  { value: 'inverse', label: 'Inverse' },
  { value: 'system', label: 'Système' },
  { value: 'power', label: 'Puissances' },
];

/** les textes d'une matrice → matrice exacte et cases illisibles */
function read(texts: string[][]): { M: Mat | null; bad: boolean[][] } {
  const vals = texts.map((r) => r.map(parseQ));
  const bad = vals.map((r) => r.map((v) => v === null));
  return { M: bad.some((r) => r.some(Boolean)) ? null : (vals as Mat), bad };
}

const F = ({ tex }: { tex: string }) => (
  <MathText className="text-ink-900 block overflow-x-auto py-0.5">{`$${tex}$`}</MathText>
);

/** « 2x + y », « -x + 0{,}5y » */
function linTex(a: Q, b: Q): string {
  const term = (c: Q, v: string, first: boolean) => {
    if (isZero(c)) return '';
    const s = show(c);
    const absTex = s.tex.replace(/^-/, '');
    const body = absTex === '1' ? v : `${absTex}${v}`;
    if (first) return c.n < 0n ? `-${body}` : body;
    return ` ${c.n < 0n ? '-' : '+'} ${body}`;
  };
  const first = term(a, 'x', true);
  const out = `${first}${term(b, 'y', !first)}`;
  return out || '0';
}

const BAD_NOTE =
  'Une case n’est pas un nombre : écris un entier (−3), un décimal (2,5, six décimales au plus) ou une fraction (3/4).';

export function MatrixCalcTool({ onSend, sendCost }: ToolProps) {
  const [mode, setMode] = useState<Mode>('product');
  const [a, setA] = useState([
    ['2', '1'],
    ['5', '3'],
  ]);
  const [b, setB] = useState([
    ['1', '−1'],
    ['0', '2'],
  ]);
  const [y, setY] = useState([['5'], ['13']]);
  const [u, setU] = useState([['1'], ['0']]);
  const [cell, setCell] = useState<[number, number]>([0, 0]);
  const [ba, setBa] = useState(false);
  const [n, setN] = useState(3);

  const A = read(a);
  const B = read(b);
  const Y = read(y);
  const U = read(u);

  return (
    <div className="space-y-4">
      <Segmented ariaLabel="Calcul" value={mode} options={MODES} onChange={setMode} />

      {mode === 'product' && (
        <div className="space-y-3">
          <MatrixInput name="A" texts={a} onChange={setA} bad={A.bad} glowRow={cell[0]} />
          <MatrixInput name="B" texts={b} onChange={setB} bad={B.bad} glowCol={cell[1]} />
          {A.M && B.M ? (
            (() => {
              const AB = matMul(A.M, B.M);
              const BA = matMul(B.M, A.M);
              const r = AB[cell[0]]![cell[1]]!;
              return (
                <div className="space-y-3">
                  <Live
                    text={`Case ligne ${cell[0] + 1}, colonne ${cell[1] + 1} de AB : ${show(r).plain}`}
                  />
                  <MatrixGrid
                    name="AB"
                    values={AB.map((row) =>
                      row.map((x) => `${show(x).exact ? '' : '≈ '}${show(x).plain}`),
                    )}
                    selected={cell}
                    onCell={(i, j) => setCell([i, j])}
                    cellLabel={(i, j) =>
                      `Case ligne ${i + 1}, colonne ${j + 1} de AB : ${show(AB[i]![j]!).plain}`
                    }
                  />
                  <section
                    className="border-ink-100 rounded-2xl border px-3 py-2"
                    data-testid="mx-cell"
                  >
                    <div className="flex min-h-11 items-center gap-2">
                      <p className="text-ink-600 min-w-0 flex-1 text-sm font-semibold">
                        Ligne {cell[0] + 1} de A × colonne {cell[1] + 1} de B
                      </p>
                      {onSend && atoms(r).length > 0 && (
                        <SendToSheetButton
                          label={show(r).plain}
                          cost={sendCost}
                          onClick={() => onSend({ atoms: atoms(r) })}
                        />
                      )}
                    </div>
                    <F
                      tex={`(AB)_{${cell[0] + 1}${cell[1] + 1}} = ${cellTex(A.M, B.M, cell[0], cell[1])}`}
                    />
                    <p className="text-ink-600 text-sm">
                      {frenchSpaces(
                        'On multiplie terme à terme la ligne de A par la colonne de B, puis on ajoute. Touche une autre case de AB.',
                      )}
                    </p>
                  </section>
                  <ToggleChip on={ba} onClick={() => setBa((v) => !v)}>
                    {frenchSpaces('Et BA ?')}
                  </ToggleChip>
                  {ba && (
                    <div className="space-y-2" data-testid="mx-ba">
                      <F tex={`BA = ${matTex(BA).tex}`} />
                      <Note tone={matEq(AB, BA) ? 'success' : 'neutral'}>
                        {frenchSpaces(
                          matEq(AB, BA)
                            ? 'Ici AB = BA : A et B commutent, c’est un cas particulier.'
                            : 'AB ≠ BA : le produit de matrices n’est pas commutatif, l’ordre compte.',
                        )}
                      </Note>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <Note tone="error">{frenchSpaces(BAD_NOTE)}</Note>
          )}
        </div>
      )}

      {mode === 'inverse' && (
        <div className="space-y-3">
          <MatrixInput name="A" texts={a} onChange={setA} bad={A.bad} />
          {A.M ? (
            (() => {
              const M = A.M;
              const d = det2(M);
              const [[p, r], [s, t]] = M as [[Q, Q], [Q, Q]];
              const inv = inv2(M);
              return (
                <div className="space-y-2" data-testid="mx-inverse">
                  <Live
                    text={`det A = ${show(d).plain}${inv ? ', A est inversible' : ', A n’est pas inversible'}`}
                  />
                  <section className="border-ink-100 rounded-2xl border px-3 py-2">
                    <div className="flex min-h-11 items-center gap-2">
                      <p className="text-ink-600 min-w-0 flex-1 text-sm font-semibold">
                        Déterminant
                      </p>
                      {onSend && atoms(d).length > 0 && (
                        <SendToSheetButton
                          label={show(d).plain}
                          cost={sendCost}
                          onClick={() => onSend({ atoms: atoms(d) })}
                        />
                      )}
                    </div>
                    <F
                      tex={`\\det A = ad - bc = ${factorTex(p)} \\times ${factorTex(t)} - ${factorTex(r)} \\times ${factorTex(s)} = ${show(d).tex}`}
                    />
                  </section>
                  {inv ? (
                    <>
                      <F
                        tex={`A^{-1} = \\frac{1}{\\det A}\\begin{pmatrix} d & -b \\\\ -c & a \\end{pmatrix} = ${matTex(inv).tex}`}
                      />
                      <F tex={`A\\,A^{-1} = ${matTex(matMul(M, inv)).tex} = I_2`} />
                      <Note tone="success">
                        {frenchSpaces('det A ≠ 0 : A est inversible, et A × A⁻¹ = A⁻¹ × A = I₂.')}
                      </Note>
                    </>
                  ) : (
                    <Note tone="error">
                      {frenchSpaces(
                        'det A = 0 : A n’est pas inversible (ses deux colonnes sont proportionnelles).',
                      )}
                    </Note>
                  )}
                </div>
              );
            })()
          ) : (
            <Note tone="error">{frenchSpaces(BAD_NOTE)}</Note>
          )}
        </div>
      )}

      {mode === 'system' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <MatrixInput name="A" texts={a} onChange={setA} bad={A.bad} />
            <MatrixInput name="Y" texts={y} onChange={setY} bad={Y.bad} />
          </div>
          {A.M && Y.M ? (
            (() => {
              const M = A.M;
              const yv = Y.M.map((r) => r[0]!);
              const X = solve(M, yv);
              const inv = inv2(M);
              return (
                <div className="space-y-2" data-testid="mx-system">
                  <Live
                    text={
                      X
                        ? `x = ${show(X[0]!).plain}, y = ${show(X[1]!).plain}`
                        : 'Pas de solution unique'
                    }
                  />
                  <F
                    tex={`\\begin{cases} ${linTex(M[0]![0]!, M[0]![1]!)} = ${show(yv[0]!).tex} \\\\ ${linTex(M[1]![0]!, M[1]![1]!)} = ${show(yv[1]!).tex} \\end{cases}`}
                  />
                  <F
                    tex={`\\iff AX = Y \\quad \\text{avec}\\ X = \\begin{pmatrix} x \\\\ y \\end{pmatrix}`}
                  />
                  {X && inv ? (
                    <>
                      <F
                        tex={`X = A^{-1}Y = ${matTex(inv).tex}${matTex(Y.M).tex} = ${matTex(X.map((v) => [v])).tex}`}
                      />
                      <Note tone="success">
                        {frenchSpaces(
                          `A est inversible : une seule solution, x = ${show(X[0]!).plain} et y = ${show(X[1]!).plain}.`,
                        )}
                      </Note>
                    </>
                  ) : (
                    <Note tone="error">
                      {frenchSpaces(
                        'det A = 0 : A n’est pas inversible, le système n’a pas une solution unique (aucune ou une infinité).',
                      )}
                    </Note>
                  )}
                </div>
              );
            })()
          ) : (
            <Note tone="error">{frenchSpaces(BAD_NOTE)}</Note>
          )}
        </div>
      )}

      {mode === 'power' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <MatrixInput name="A" texts={a} onChange={setA} bad={A.bad} />
            <MatrixInput name="U₀" texts={u} onChange={setU} bad={U.bad} />
          </div>
          <div className="max-w-56">
            <Stepper label="n" value={n} min={0} max={20} onChange={setN} />
          </div>
          {A.M && U.M ? (
            (() => {
              const P = matPow(A.M, n);
              const Pt = matTex(P);
              const Un = matMul(P, U.M);
              const Ut = matTex(Un);
              const exact = Pt.exact && Ut.exact;
              const eqs = (t: { exact: boolean }) => (t.exact ? '=' : '\\approx');
              return (
                <div className="space-y-2" data-testid="mx-power">
                  <Live
                    text={`U${subscript(n)} = (${Un.map((r) => show(r[0]!).plain).join(' ; ')})`}
                  />
                  <F tex={`A^{${n}} ${n === 0 ? '= I_2 ' : ''}${eqs(Pt)} ${Pt.tex}`} />
                  <F
                    tex={`U_{n+1} = A\\,U_n \\implies U_{${n}} = A^{${n}}\\,U_0 ${eqs(Ut)} ${Ut.tex}`}
                  />
                  <p className="text-ink-600 text-sm">
                    {frenchSpaces(
                      `Par récurrence : U₁ = AU₀, U₂ = AU₁ = A²U₀… donc U${subscript(n)} = A${superscript(n)}U₀.${exact ? '' : ' Les nombres deviennent trop longs : valeurs arrondies (≈).'}`,
                    )}
                  </p>
                </div>
              );
            })()
          ) : (
            <Note tone="error">{frenchSpaces(BAD_NOTE)}</Note>
          )}
        </div>
      )}
    </div>
  );
}

const SUB = '₀₁₂₃₄₅₆₇₈₉';
const subscript = (k: number) =>
  String(k)
    .split('')
    .map((c) => SUB[Number(c)])
    .join('');
